import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PageHero from "../components/PageHero";
import LocationSwitcher from "../components/LocationSwitcher";
import LocationAtlas from "../components/LocationAtlas";
import LocationProjects from "../components/LocationProjects";
import Contact from "../components/Contact";
import useScrollReveal, { revealUp } from "../hooks/useScrollReveal";
import { locations } from "../data/siteData";
import "./LocationsPage.css";

export default function LocationsPage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const initial = locations.find((l) => l.slug === slug) || locations[0];
  const [activeId, setActiveId] = useState(initial.id);

  useEffect(() => {
    const found = locations.find((l) => l.slug === slug);
    if (found) setActiveId(found.id);
  }, [slug]);

  const active = locations.find((l) => l.id === activeId);

  const detailRef = useScrollReveal(({ gsap, el }) => {
    gsap.from(el.querySelector(".location-detail__grid"), revealUp(null, el));

    // Subtle parallax on the location photo as the page scrolls.
    gsap.to(el.querySelector(".location-detail__image"), {
      backgroundPosition: "center 65%",
      ease: "none",
      scrollTrigger: {
        trigger: el,
        start: "top bottom",
        end: "bottom top",
        scrub: 0.6,
      },
    });
  }, [activeId]);

  const handleChange = (id) => {
    setActiveId(id);
    const loc = locations.find((l) => l.id === id);
    navigate(`/locations/${loc.slug}`, { replace: true });
  };

  if (!slug) {
    return (
      <>
        <PageHero
          eyebrow="Our Locations"
          title={["ONE REGISTERED", "OFFICE. ONE", "BRANCH OFFICE."]}
          subtitle="Explore either Selectra Technologies location for local contact details and coverage."
        />
        <LocationAtlas />
      </>
    );
  }

  return (
    <>
      <section className="location-detail" ref={detailRef}>
        <div className="location-detail__disc" aria-hidden="true" />
        <div className="container location-detail__inner">
          <span className="eyebrow">{active.label || `Location ${active.code}`}</span>
          <h1 className="location-detail__title">{active.city}</h1>

          <LocationSwitcher activeId={activeId} onChange={handleChange} />

          <div className="location-detail__grid">
            <div
              className="location-detail__image"
              style={active.image ? { backgroundImage: `url(${active.image})` } : undefined}
            />
            <div className="location-detail__info">
              <p className="location-detail__description">
                {active.description}
              </p>
              <dl>
                <div>
                  <dt>Address</dt>
                  <dd>{active.address}</dd>
                </div>
                <div>
                  <dt>Phone</dt>
                  <dd>{active.phone}</dd>
                </div>
                <div>
                  <dt>Email</dt>
                  <dd>{active.email}</dd>
                </div>
                <div>
                  <dt>Hours</dt>
                  <dd>{active.hours}</dd>
                </div>
              </dl>
              <div className="location-detail__stat">
                <span className="accent-text">{active.stat.value}</span>
                <span>{active.stat.label}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <LocationProjects
        key={active.id}
        locationId={active.id}
        locationName={active.city}
        seedProjects={active.projects || []}
      />

      <Contact />
    </>
  );
}
